import React, { useEffect, useRef, useState } from "react";
import Footer from "../../../layout/Footer";
import Header from "../../../layout/Header";
import Sidebar from "../../../layout/Sidebar";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";

export default function EditPlan() {

  const token = localStorage.getItem("adminToken");
  const API_URL = process.env.REACT_APP_BASE_URL_API || "";
  const planId = window.location.pathname.split("/").pop();

  const fetchedRef = useRef(false);

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [plan, setPlan] = useState({
    name: "",
    display_plan_price: "",
    price: "",
    agentCommission: "",
    features: ""
  });

  /* ================= FETCH PLAN (RUN ONCE) ================= */
  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    fetchPlan();
  }, []);

  const fetchPlan = async () => {
    try {
      const response = await fetch(
        `${API_URL}/api/admin/membership-plan/${planId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          }
        }
      );

      const result = await response.json();

      if (result && result.data) {
        setPlan({
          name: result.data.name ?? "",
          display_plan_price: result.data.display_plan_price ?? "",
          price: result.data.price ?? "",
          agentCommission: result.data.agentCommission ?? "",
          features: result.data.features ?? ""
        });
      }
    } catch (error) {
      console.error("Fetch error:", error);
    }
  };

  /* ================= HANDLE INPUT CHANGE ================= */
  const handleChange = (e) => {
    const { name, value } = e.target;
    const nextPlan = {
      ...plan,
      [name]: value
    };

    const nextErrors = { ...errors };
    if (nextErrors[name]) {
      nextErrors[name] = "";
    }

    if (name === "display_plan_price" || name === "price") {
      const displayPlanPrice = nextPlan.display_plan_price;
      const discountedPrice = nextPlan.price;

      if (
        displayPlanPrice !== "" &&
        discountedPrice !== "" &&
        Number(displayPlanPrice) <= Number(discountedPrice)
      ) {
        nextErrors.display_plan_price = "Display price must be greater than discounted price";
      } else {
        nextErrors.display_plan_price = "";
      }
    }

    setPlan(nextPlan);
    setErrors(nextErrors);
  };

  /* ================= VALIDATION ================= */
  const validateForm = () => {
    let newErrors = {};

    if (plan.price === "") {
      newErrors.price = "Discounted price is required";
    }

    if (plan.display_plan_price === "") {
      newErrors.display_plan_price = "Display price is required";
    } else if (
      plan.price !== "" &&
      Number(plan.display_plan_price) <= Number(plan.price)
    ) {
      newErrors.display_plan_price = "Display price must be greater than discounted price";
    }

    if (
      plan.agentCommission === "" ||
      isNaN(plan.agentCommission) ||
      Number(plan.agentCommission) > 10000
    ) {
      newErrors.agentCommission = "Commission must be between 0 and 10000";
    }

    const plainText = plan.features.replace(/<[^>]*>/g, "").trim();
    if (!plainText) {
      newErrors.features = "Plan features are required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /* ================= UPDATE PLAN ================= */
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);

    try {
      const response = await fetch(
        `${API_URL}/api/admin/membership-plan/update/${planId}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          },
          body: JSON.stringify(plan)
        }
      );

      const result = await response.json();

      if (response.ok) {
        window.location.href = "/admin/membership/view-plan";
      } else {
        alert(result.message || "Update failed");
      }
    } catch (error) {
      console.error("Update error:", error);
    } finally {
      setLoading(false);
    }
  };

  /* ================= JSX ================= */
  return (
    <div className="container body">
      <div className="main_container">
        <Sidebar />
        <Header pageTitle="Edit Plan" />

        <div className="right_col" role="main">
        

          <form onSubmit={handleSubmit}>
            <div className="row">

              <div className="col-sm-4 mb-3">
                <label>Plan Name</label>
                <input
                  type="text"
                  name="name"
                  value={plan.name}
                  className="form-control"
                  readOnly
                />
              </div>
              <div className="col-sm-4 mb-3">
                <label>Plan Price</label>
                <input
                  type="number"
                  name="display_plan_price"
                  value={plan.display_plan_price}
                  onChange={handleChange}
                  className="form-control"
                />
                {errors.display_plan_price && (
                  <p className="text-danger">{errors.display_plan_price}</p>
                )}
              </div>

              <div className="col-sm-4 mb-3">
                <label>Discounted Plan Price</label>
                <input
                  type="number"
                  name="price"
                  value={plan.price}
                  onChange={handleChange}
                  className="form-control"
                />
                {errors.price && (
                  <p className="text-danger">{errors.price}</p>
                )}
              </div>

              <div className="col-sm-4 mb-3">
                <label>Agent Commission</label>
                <input
                  type="number"
                  name="agentCommission"
                  value={plan.agentCommission}
                  onChange={handleChange}
                  className="form-control"
                  placeholder="0 - 10000"
                />
                {errors.agentCommission && (
                  <p className="text-danger">{errors.agentCommission}</p>
                )}
              </div>

              <div className="col-sm-12 mb-3">
                <label>Plan Features</label>

                <CKEditor
                  key={planId}
                  editor={ClassicEditor}
                  data={plan.features}
                  onChange={(event, editor) => {
                    const data = editor.getData();
                    setPlan(prev => ({ ...prev, features: data }));
                    if (errors.features) {
                      setErrors(prev => ({ ...prev, features: "" }));
                    }
                  }}
                />

                {errors.features && (
                  <p className="text-danger">{errors.features}</p>
                )}
              </div>

              <div className="col-12 text-right">
                <button
                  type="submit"
                  className="btn sitebtn"
                  disabled={loading}
                >
                  {loading ? "Updating..." : "Update Plan"}
                </button>
              </div>

            </div>
          </form>
        </div>

        <Footer />
      </div>
    </div>
  );
}
